import { useQueryClient } from '@tanstack/react-query'
import { Plus, SaveIcon } from 'lucide-react'
import React, { useContext, useEffect } from 'react'
import { type SubmitHandler, useForm } from 'react-hook-form'

import NeedsAttentionContext from 'components/context/NeedsAttentionContext'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from 'components/ui/sheet'
import { Badge, Checkbox, CheckboxGroup } from 'slab'
import { useGetBrokersMembersMemberIdEnrollmentsMappingOptions } from 'utils/web_api'
import { type BrokersEnrollmentMappingOption } from 'utils/web_api.schemas'

import AssigneesFilter from './AssigneesFilter'
import TagFilter from './TagsFilter'
import {
  type MissingPaymentsRow,
  type MissingPaymentStatus
} from './types'
import { type GrapeAPIErrorType } from '../../hooks/GrapeAPIError'
import { useCreateOrEditNAVItem } from '../../hooks/needs_attention/useCreateOrEditNAVItem'
import { Banner } from '../../reusable/Banner'
import { FormSelectField, FormTextArea } from '../../reusable/form/FormFields'
import { Button } from '../../ui/button'

export type CreateOrEditNAVOptionsState = {
  isOpen: boolean
  item?: MissingPaymentsRow | undefined
}

type CreateOrEditNAVItemProps = {
  item?: MissingPaymentsRow
  isOpen: boolean
  setIsOpen: React.Dispatch<React.SetStateAction<CreateOrEditNAVOptionsState>>
  memberId?: string
}

type CreateOrEditNAVItemInput = {
  reason: string
  status: MissingPaymentStatus
  enrollment_ids: string[]
  broker_ids: string[]
  tag_ids: string[]
  notes: string
}

const getCoverageOrder = (coverageType: string | undefined): number => {
  switch (coverageType) {
    case 'medical':
      return 0
    case 'dental':
      return 1
    case 'vision':
      return 2
    default:
      return 3
  }
}

const sortInfo = (a: BrokersEnrollmentMappingOption, b: BrokersEnrollmentMappingOption) => {
  // Sort by plan_year descending
  if (a.plan_year !== b.plan_year) {
    return (b.plan_year || 0) - (a.plan_year || 0)
  }

  // Then by coverage_type using the custom order
  return getCoverageOrder(a.coverage_type) - getCoverageOrder(b.coverage_type)
}

const currentYear = new Date().getFullYear()
const CREATE_DESCRIPTION = `
  Provide the below details to create a new NAV item.
  These NAV items are meant to help us process non-payment related tasks that require manual
  attention.
`

const CreateOrEditNAVItem = ({
  item,
  isOpen,
  setIsOpen,
  memberId,
}: CreateOrEditNAVItemProps) => {
  const queryClient = useQueryClient()
  const { getBrokersMissingPaymentsQueryKey } = useContext(
    NeedsAttentionContext
  )

  const defaultValues: CreateOrEditNAVItemInput = {
    reason: item?.reason || '',
    status: item?.status || 'active',
    tag_ids: item?.tags?.map((tag) => tag.id) || [],
    broker_ids: item?.brokers?.map((broker) => broker.id) || [],
    notes: item?.notes || '',
    enrollment_ids: [],
  }

  const {
    register,
    unregister,
    handleSubmit,
    setValue,
    formState,
    reset,
    watch,
  } = useForm<CreateOrEditNAVItemInput>({
    defaultValues,
    mode: 'all',
  })

  useEffect(() => {
    if (item) {
      // Only the reason and notes are editable from this form. Otherwise, brokers
      // should be using the other available UI methods for editing assignees and tags.
      setValue('reason', item.reason)
      setValue('status', item?.status || 'active')
      setValue('notes', item?.notes || '')
      unregister('enrollment_ids')
    }
  }, [item])

  // Mapping options are only used when creating NAV items.
  // When editing, the enrollments have already been attached to the NAV item, so we can ignore errors
  // when the memberId is not available.
  const { data: mappingOptions, isLoading } = useGetBrokersMembersMemberIdEnrollmentsMappingOptions(
    memberId || '',
    {
      query: {
        enabled: !!memberId,
        retry: false,
      }
    }
  )

  const sortedMappingOptions = mappingOptions?.sort(sortInfo)

  const [error, setError] = React.useState<GrapeAPIErrorType | undefined>(
    undefined
  )

  const isCreate = !item
  const createNAVItemMutation = useCreateOrEditNAVItem({ id: item?.id })

  const toggleState = (
    key: keyof Pick<CreateOrEditNAVItemInput, 'broker_ids' | 'tag_ids'>,
    value: string
  ) => {
    const currentValue = watch(key)
    if (currentValue.includes(value)) {
      setValue(
        key,
        currentValue.filter((v) => v !== value)
      )
    } else {
      setValue(key, [...currentValue, value])
    }
  }

  const onSubmit: SubmitHandler<CreateOrEditNAVItemInput> = async (params) => {
    createNAVItemMutation.mutate(params, {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getBrokersMissingPaymentsQueryKey,
        })
        setIsOpen({ isOpen: false, item: undefined })
        setError(undefined)
        reset()
      },
      onError: (error: GrapeAPIErrorType) => {
        setError(error)
        console.error(error)
      },
    })
  }

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      reset()
      setIsOpen((prev) => ({ ...prev, item: undefined }))
    }
    setIsOpen((prev) => ({ ...prev, isOpen: open }))
  }

  const isSubmitDisabled = formState.isSubmitting || !formState.isValid
  const reasonOptions = [
    { label: 'Incorrect Configuration', value: 'incorrect_configuration' },
    { label: 'Payments Issue', value: 'payments_issue' },
    { label: 'Backdate Requested', value: 'backdate_requested' },
    { label: 'Offboarding', value: 'offboarding' },
    { label: 'Other', value: 'other' },
  ]

  // This does not include "silenced" because there's a separate UI for that.
  const statusOptions = [
    { label: 'Active', value: 'active' },
    { label: 'Resolved', value: 'resolved' },
    { label: 'Silenced', value: 'silenced', disabled: true },
  ]
  const selectedAssignees = watch('broker_ids')
  const selectedTags = watch('tag_ids')

  return (
    <div className='space-y-4'>
      <Sheet open={isOpen} onOpenChange={handleOpenChange}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle className='pb-10'>
              <span className='heading-lg'>
                {isCreate
                  ? 'Create Needs Attention Item'
                  : 'Edit Needs Attention Item'}
              </span>
            </SheetTitle>
          </SheetHeader>
          {error && (
            <Banner
              type='critical'
              titleText={error.message || 'Error creating nav item'}
            >
              {error.errors?.map((error) => (
                <div key={error}>{error}</div>
              ))}
            </Banner>
          )}
          <form onSubmit={handleSubmit(onSubmit)} className='space-y-4'>
            <SheetDescription className='text-sm font-light mt-1 p-1'>
              {isCreate
                ? CREATE_DESCRIPTION
                : 'Edit the NAV item details below.'}
            </SheetDescription>

            <div>
              <FormSelectField
                fieldId='reason'
                fieldLabel='Reason'
                fieldOptions={reasonOptions}
                register={register}
                validationRule={{ required: 'Reason is required' }}
              />
              <div className='text-sm font-light mt-1 p-1'>
                Only non-payment related reasons can be created manually.
              </div>
            </div>

            {/* Can only update the status when editing NAV items. Otherwise its "active" by default. */}
            {!isCreate && (
              <div>
                <FormSelectField
                  fieldId='status'
                  fieldLabel='Status'
                  fieldOptions={statusOptions}
                  register={register}
                  validationRule={{ required: 'Status is required' }}
                />
                <div className='text-sm font-light mt-1 p-1'>
                  If the NAV item has been completed, please mark it as
                  resolved.
                </div>
              </div>
            )}

            {/* Can only set enrollments, tags and assignees when creating the NAV item. Otherwise there's already
                a UI for updating these values for already created NAV items.
              */}
            {isCreate && (
              <>
                <div>
                  <CheckboxGroup>
                    <label
                      className='label-default font-medium text-ds-text-secondary'
                      htmlFor='enrollment_ids'
                    >
                      Enrollments
                    </label>
                    <div className='text-sm font-light my-1'>
                      Select which enrollments the NAV items should be applied
                      to. One NAV item will be created for each enrollment
                      selected.
                    </div>
                    {sortedMappingOptions?.map((option) => (
                      <Checkbox
                        key={option.id}
                        id={option.id}
                        value={option.id}
                        disabled={isLoading}
                        data-testid={`enrollment-${option.id}`}
                        label={
                          <div className='space-x-1 flex items-center'>
                            <Badge
                              type={
                                option.plan_year === currentYear
                                  ? 'info'
                                  : 'neutral'
                              }
                            >
                              {option.plan_year}
                            </Badge>
                            <div className='text-wrap'>
                              {[option.carrier_name, option.plan_name].join(
                                ' | '
                              )}
                            </div>
                          </div>
                        }
                        {...register('enrollment_ids', {
                          required: 'Enrollment IDs are required',
                        })}
                      />
                    ))}
                  </CheckboxGroup>
                </div>

                <div className='space-y-2'>
                  <label
                    className='label-default font-medium text-ds-text-secondary'
                    htmlFor='enrollment_ids'
                  >
                    Assignees
                  </label>
                  <AssigneesFilter
                    selectedValues={selectedAssignees}
                    onSelectValue={(value) => toggleState('broker_ids', value)}
                  />
                </div>

                <div className='space-y-2'>
                  <label
                    className='label-default font-medium text-ds-text-secondary'
                    htmlFor='tag_ids'
                  >
                    Tags
                  </label>
                  <TagFilter
                    selectedValues={selectedTags}
                    onSelectValue={(value) => toggleState('tag_ids', value)}
                  />
                </div>
              </>
            )}

            <div>
              <FormTextArea
                fieldId='notes'
                fieldLabel='Note'
                register={register}
                validationRule={{ required: 'Note is required' }}
                className='h-40'
              />
              <div className='text-sm font-light mt-1 p-1'>
                Please provide some helpful context to ensure the NAV items are
                properly processed!
              </div>
            </div>
            <footer className='mt-4'>
              <Button
                type='submit'
                variant='primary'
                disabled={isSubmitDisabled}
                data-testid='submit-button'
              >
                {isCreate
                  ? (
                    <Plus size={21} className='mr-2' />
                    )
                  : (
                    <SaveIcon size={21} className='mr-2' />
                    )}
                {isCreate ? 'Create NAV Item' : 'Save changes'}
              </Button>
            </footer>
          </form>
        </SheetContent>
      </Sheet>
    </div>
  )
}

export default CreateOrEditNAVItem
